// src/hooks/useTimelineVersions.js
// Field-locked hook for timeline_versions table.
// Versions are per-project read-only snapshots of a stage timeline.
// Labels (v.01, v.02…) are computed client-side from sort order.
import { useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';

const requireSession = async () => {
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw new Error('Authentication error: ' + error.message);
  if (!session?.user) throw new Error('Please sign in to perform this action.');
  return session.user.id;
};

function mapRow(row, index) {
  return {
    id: String(row.id),
    label: 'v.' + String(index + 1).padStart(2, '0'),
    createdAt: row.created_at,
    projectId: String(row.project_id),
    projectName: row.project_name || '',
    snapshot: row.snapshot || {},
  };
}

// The Timelines (planning) page and the Timeline (billing) page share this table.
// `source` keeps each page's versions separate: 'planning' (default) vs 'billing'.
export function useTimelineVersions(source = 'planning') {
  const [versions, setVersions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch this source's versions for a project, sorted oldest→newest, with positional labels
  const fetchVersions = useCallback(async (projectId) => {
    if (!projectId) { setVersions([]); return; }
    setLoading(true);
    setError(null);
    try {
      const { data, error: err } = await supabase
        .from('timeline_versions')
        .select('id, project_id, project_name, created_at, snapshot')
        .eq('project_id', projectId)
        .eq('source', source)
        .order('created_at', { ascending: true });
      if (err) throw err;
      setVersions((data || []).map(mapRow));
    } catch (e) {
      setError(e.message || String(e));
      setVersions([]);
    } finally {
      setLoading(false);
    }
  }, [source]);

  // Save a new version snapshot for a project
  const saveVersion = useCallback(async ({ projectId, projectName, snapshot }) => {
    await requireSession();
    const { data, error: err } = await supabase
      .from('timeline_versions')
      .insert({
        project_id: Number(projectId),
        project_name: projectName || '',
        snapshot,
        source,
      })
      .select('id, project_id, project_name, created_at, snapshot')
      .single();
    if (err) throw err;
    // Re-fetch to get accurate positional labels
    await fetchVersions(projectId);
    return data;
  }, [fetchVersions, source]);

  // Delete a version by its id and refresh the list
  const deleteVersion = useCallback(async (versionId, projectId) => {
    await requireSession();
    const { error: err } = await supabase
      .from('timeline_versions')
      .delete()
      .eq('id', Number(versionId));
    if (err) throw err;
    // Re-fetch to recompute positional labels after deletion
    await fetchVersions(projectId);
  }, [fetchVersions]);

  return { versions, loading, error, fetchVersions, saveVersion, deleteVersion };
}
